import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPaymentStatus, generateNatalReport, generateCompatibilityReport, generateDashaReport } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ReportDetailsForm } from "@/components/ReportDetailsForm";

const REPORT_PACKAGES = {
  natal_report: { label: "Natal Report", generate: generateNatalReport, filename: "natal_report.pdf" },
  compatibility: { label: "Compatibility Report", generate: generateCompatibilityReport, filename: "compatibility_report.pdf" },
  compatibility_report: { label: "Compatibility Report", generate: generateCompatibilityReport, filename: "compatibility_report.pdf" },
  dasha_forecast: { label: "Dasha Forecast", generate: generateDashaReport, filename: "dasha_forecast.pdf" },
};

// Blob responses hide the JSON error body; parse it back out
async function readErrorDetail(e) {
  let data = e?.response?.data;
  if (data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch { return null; }
  }
  return data?.detail ?? null;
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [needsDetails, setNeedsDetails] = useState(false);
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const packageId = status?.package_id || status?.metadata?.package_id;
  const reportConfig = REPORT_PACKAGES[packageId];
  const isKarmic = packageId === "karmic_access_report" || packageId === "karmic_access_report_premium";

  const downloadReport = useCallback(async (details = {}) => {
    if (!reportConfig || !sessionId) return;
    setGenerating(true);
    try {
      const blob = await reportConfig.generate({ session_id: sessionId, ...details });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportConfig.filename;
      a.click();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setNeedsDetails(false);
      toast.success("Report downloaded!");
    } catch (e) {
      const detail = await readErrorDetail(e);
      if (detail?.requires_birth_details) {
        setNeedsDetails(true);
        toast.info("Please enter the birth details for your report");
      } else {
        toast.error(detail?.message || (typeof detail === "string" ? detail : null) || "Could not generate the report. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  }, [reportConfig, sessionId]);

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    getPaymentStatus(sessionId)
      .then((data) => setStatus(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sessionId]);

  // Auto-download the report once payment is confirmed paid
  useEffect(() => {
    if (!loading && status?.payment_status === "paid" && reportConfig && !downloaded && !generating) {
      downloadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, status, reportConfig]);

  // Karmic report: once paid, send the user to the Karmic page to auto-generate
  useEffect(() => {
    if (!loading && status?.payment_status === "paid" && isKarmic && sessionId) {
      navigate(`/karmic?session_id=${sessionId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, status, isKarmic, sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md px-6 text-center"
      >
        {loading ? (
          <img src="/logo.png" alt="Daily Nakshatra" className="h-10 w-auto mx-auto logo-pulse" />
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-light text-white/95 mb-3" data-testid="payment-success-title">Payment Successful</h1>
            <p className="text-white/40 text-sm mb-2">
              {status?.metadata?.package_name || "Your purchase"} is confirmed.
            </p>
            {status?.amount_total && (
              <p className="text-white/30 text-xs mb-8">
                ${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}
              </p>
            )}
            <div className="space-y-3">
              {/* Missing birth details: collect them here and generate with the paid session */}
              {reportConfig && needsDetails && (
                <ReportDetailsForm
                  packageId={packageId}
                  label={reportConfig.label}
                  initial={status?.metadata || {}}
                  onSubmit={downloadReport}
                  submitting={generating}
                />
              )}
              {/* One-time report purchase: auto-download + manual re-download */}
              {reportConfig && !needsDetails && (
                <Button
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-11 text-sm"
                  onClick={() => downloadReport()}
                  disabled={generating}
                  data-testid="success-download-report-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating your {reportConfig.label}…</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> {downloaded ? `Download ${reportConfig.label} Again` : `Download ${reportConfig.label}`}</>
                  )}
                </Button>
              )}
              {(packageId === "premium_monthly" || packageId === "premium_annual") && (
                <Button
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-11 text-sm"
                  onClick={() => navigate("/")}
                  data-testid="success-dashboard-btn"
                >
                  Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              <Link to="/">
                <Button variant="ghost" className="w-full text-white/40 hover:text-white text-sm" data-testid="success-home-btn">
                  Return Home
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
